import { useEffect, useRef, useState } from "react";
import { MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { api, errorText } from "@/lib/api";
import { PageTitle } from "@/components/Brand";
import FounderReview from "@/components/workspace/FounderReview";
import StrategyRoomCTA from "@/components/workspace/StrategyRoomCTA";
import {
  ActivePlanCard,
  ManualPaymentCard,
  OrderHistory,
  PricingCard,
  QuoteSummary,
  billingKind,
  canonicalTier,
  text,
} from "./GrowthClubView";

const OPEN_PAYMENT = ["awaiting_receipt", "pending_verification", "approving"];

function entitlementMatches(sub, state, order) {
  if (!sub || !order || order.payment_status !== "paid") return false;
  const access = state?.access_status;
  if (access !== "active" && access !== "expiring") return false;
  if (canonicalTier(sub.package) !== canonicalTier(order.package)) return false;
  if (order.billing_period && sub.billing_period && order.billing_period !== sub.billing_period) return false;
  return true;
}

function deriveQuoteStatus(quote, order, { sameOwnedPackage, paidThisQuote }) {
  if (!quote?.status) return null;
  if (quote.status !== "approved") return quote.status;
  const payment = order?.payment_status;
  if (OPEN_PAYMENT.includes(payment) || payment === "rejected") return payment;
  if (payment === "paid" || paidThisQuote) return "paid";
  if (sameOwnedPackage) return "active";
  return "approved_unpaid";
}

export default function Subscription({ lang, t, data, setData, checkout, clearCheckout }) {
  const [info, setInfo] = useState(null);
  const [proposals, setProposals] = useState([]);
  const [pricingQuote, setPricingQuote] = useState(null);
  const [manualConfig, setManualConfig] = useState(null);
  const [manualOrder, setManualOrder] = useState(null);
  const [telegramUrl, setTelegramUrl] = useState("https://t.me/Noorin_strategy");
  const [receiptUrl, setReceiptUrl] = useState("");
  const [tier, setTier] = useState((sessionStorage.getItem("noorin-tier") === "Strategic" ? "Premium" : sessionStorage.getItem("noorin-tier")) || "Standard");
  const [busy, setBusy] = useState("");
  const [verifying, setVerifying] = useState(Boolean(checkout && checkout.payment === "success"));
  const handled = useRef(false);
  const copy = text(lang);

  const load = () => api.get("/subscription").then((r) => {
    setInfo(r.data);
    setManualOrder(r.data.orders?.find((order) => order.provider === "manual_card" && OPEN_PAYMENT.includes(order.payment_status)) || null);
    setData((d) => ({ ...d, subscription: r.data.subscription }));
  });
  useEffect(() => {
    load().catch(() => setInfo({ subscription: null, orders: [], providers: {} }));
    api.get("/services").then((r) => setProposals(r.data.proposals)).catch(() => {});
    api.get("/pricing-quote").then((r) => { setPricingQuote(r.data.quote); setTelegramUrl(r.data.telegram_url || telegramUrl); }).catch(() => {});
    api.get("/manual-payment/config").then((r) => setManualConfig(r.data)).catch(() => setManualConfig({ ready: false }));
    const refresh = () => load().catch(() => {});
    const timer = setInterval(refresh, 60000);
    window.addEventListener("focus", refresh);
    return () => { clearInterval(timer); window.removeEventListener("focus", refresh); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!checkout || handled.current) return;
    handled.current = true;
    const finish = (ok, msg) => { setVerifying(false); (ok ? toast.success : toast.message)(msg); clearCheckout(); load().catch(() => {}); };
    if (checkout.payment === "cancelled") { finish(false, t.paymentCancelled); return; }
    if (checkout.provider === "paypal" && checkout.token) {
      api.post(`/checkout/paypal/capture/${checkout.token}`).then((r) => finish(r.data.payment_status === "paid", r.data.payment_status === "paid" ? t.paymentSuccess : t.paymentPending)).catch((err) => finish(false, errorText(err, t.paymentPending)));
      return;
    }
    if (checkout.provider === "stripe" && checkout.sessionId) {
      let attempts = 0;
      const poll = async () => {
        try {
          const r = await api.get(`/checkout/status/${checkout.sessionId}`);
          if (r.data.payment_status === "paid") return finish(true, t.paymentSuccess);
          if (r.data.status === "expired" || ++attempts >= 5) return finish(false, t.paymentPending);
          setTimeout(poll, 2000);
        } catch (err) { finish(false, errorText(err, (err.response?.status === 409 ? (lang === "fa" ? "پرداخت قدیمی نیازمند بررسی تیم نورین است." : "Legacy payment requires review by the NOORIN team.") : t.paymentPending))); }
      };
      poll();
      return;
    }
    finish(false, t.paymentPending);
  }, [checkout]); // eslint-disable-line react-hooks/exhaustive-deps

  const receiptOrderId = manualOrder?.payment_status === "awaiting_receipt" ? manualOrder.order_id : "";
  useEffect(() => {
    if (!receiptOrderId) { setReceiptUrl(""); return undefined; }
    let cancelled = false;
    api.get(`/manual-payment/orders/${receiptOrderId}`).then((r) => {
      if (!cancelled) setReceiptUrl(r.data?.telegram_url || "");
    }).catch(() => { if (!cancelled) setReceiptUrl(""); });
    return () => { cancelled = true; };
  }, [receiptOrderId]);

  const requestPricingQuote = async (kind = "package") => {
    setBusy("quote");
    try {
      const r = await api.post("/pricing-quote/generate", { tier, lang, kind });
      setPricingQuote(r.data.quote);
      setTelegramUrl(r.data.telegram_url || telegramUrl);
      load().catch(() => {});
      toast.success(copy.quoteRecorded);
    } catch (err) { toast.error(errorText(err, t.quoteUnavailable)); }
    finally { setBusy(""); }
  };

  const createManualPaymentOrder = async () => {
    if (!pricingQuote?.id) return;
    setBusy("manual-payment");
    try {
      const r = await api.post("/manual-payment/orders", { quote_id: pricingQuote.id });
      setManualOrder(r.data.order);
      setManualConfig(r.data);
      window.open(r.data.telegram_url, "_blank", "noopener,noreferrer");
      toast.success(lang === "fa" ? "سفارش ثبت شد؛ فیش را در بات نورین ارسال کنید." : "Order created. Send the receipt in the NOORIN bot.");
      load().catch(() => {});
    } catch (err) { toast.error(errorText(err, lang === "fa" ? "ثبت سفارش پرداخت ممکن نیست." : "Could not create the payment order.")); }
    finally { setBusy(""); }
  };

  if (!info) return <p className="muted">{t.loading}</p>;
  const sub = info.subscription;
  const state = info.subscription_state || { status: "none", access_status: "none" };
  const orders = info.orders || [];
  const live = state.access_status === "active" || state.access_status === "expiring";
  const ownedTier = sub && live && billingKind(sub.billing_period) === "one_time" ? canonicalTier(sub.package) : null;
  const sameOwnedPackage = Boolean(ownedTier && pricingQuote?.kind === "package" && canonicalTier(pricingQuote.tier) === ownedTier);
  const paidThisQuote = orders.some((order) => pricingQuote?.id && order.quote_id === pricingQuote.id && entitlementMatches(sub, state, order));
  const suppressNewPayment = sameOwnedPackage || paidThisQuote;
  const relatedOrders = orders.filter((order) => pricingQuote?.id && order.quote_id === pricingQuote.id);
  const quoteOrder = (manualOrder && manualOrder.quote_id === pricingQuote?.id ? manualOrder : null) || relatedOrders[0] || null;
  const statusKey = deriveQuoteStatus(pricingQuote, quoteOrder, { sameOwnedPackage, paidThisQuote });
  const quoteApproved = pricingQuote?.status === "approved";
  const canCreateOrder = quoteApproved && !manualOrder && !suppressNewPayment;
  const showManual = Boolean(manualOrder) || (quoteApproved && !suppressNewPayment);
  const sameTierSelected = Boolean(ownedTier && canonicalTier(tier) === ownedTier);
  const statusNote = statusKey === "active" && ownedTier ? `${copy.owned(ownedTier)} ${copy.packageNote}` : "";

  return (
    <div className="section-page wide growth-club">
      <PageTitle eyebrow={t.eyebrows.growth} title={lang === "fa" ? "باشگاه رشد" : "Growth Club"} sub={lang === "fa" ? "پکیج، پیشنهاد قیمت و پرداخت." : "Your package, quote, and payment."} />
      <StrategyRoomCTA t={t} testId="growth-strategy-room" />
      {verifying && <div className="notice" data-testid="payment-verifying">{t.verifying}</div>}
      <ActivePlanCard lang={lang} t={t} sub={sub} state={state} ownedTier={ownedTier} />
      <section className="gc-section">
        <div>
          <p className="eyebrow">{copy.packagesTitle}</p>
          <h3>{t.proposalsSub}</h3>
          <p className="muted">{copy.catalogNote}</p>
        </div>
        <div className="gc-prices" data-testid="proposals">
          {(Array.isArray(proposals) ? proposals : []).map((proposal) => (
            <PricingCard key={proposal.tier} lang={lang} t={t} proposal={proposal} selected={canonicalTier(tier) === canonicalTier(proposal.tier)} owned={ownedTier === canonicalTier(proposal.tier)} onSelect={(item) => { setTier(item.tier); sessionStorage.setItem("noorin-tier", item.tier); }} />
          ))}
        </div>
      </section>
      <section className="gc-section" data-testid="zarinpal-pricing-quote">
        <div>
          <p className="eyebrow">{copy.quoteTitle}</p>
          <h3>{lang === "fa" ? "پکیج، ارتقا یا اشتراک ماهانه" : "Package, upgrade, or monthly support"}</h3>
          <p className="muted">{copy.quoteIntro}</p>
        </div>
        <QuoteSummary lang={lang} t={t} quote={pricingQuote} statusKey={statusKey} order={quoteOrder} statusNote={statusNote} />
        <div className="row-actions">
          <button type="button" className="quiet-button" onClick={() => requestPricingQuote("package")} disabled={Boolean(busy) || sameTierSelected} title={sameTierSelected ? copy.owned(ownedTier) : undefined} data-testid="request-pricing-quote">{t.requestQuote} — {tier}</button>
          <button type="button" className="quiet-button" onClick={() => requestPricingQuote("monthly_subscription")} disabled={Boolean(busy)}>{lang === "fa" ? "درخواست قیمت اشتراک ماهانه" : "Request monthly support quote"}</button>
          <button type="button" className="quiet-button" onClick={() => requestPricingQuote("upgrade")} disabled={Boolean(busy) || sameTierSelected} title={sameTierSelected ? copy.owned(ownedTier) : undefined}>{lang === "fa" ? "بررسی ارتقا با اعتبار پرداخت قبلی" : "Request upgrade with prior-payment credit"}</button>
        </div>
        <p className="muted">{lang === "fa" ? "در صورت ارتقا، مبلغ پرداخت‌شده قبلی پس از تأیید از هزینه پکیج جدید کسر می‌شه." : "Upgrades credit verified prior payments against the new package and charge only the difference. New scope must be agreed; services outside it are not free."}</p>
        <a className="quiet-button telegram-link" href={telegramUrl} target="_blank" rel="noreferrer"><MessageCircle size={16} />{t.talkToAdvisor}</a>
      </section>
      {showManual && <ManualPaymentCard lang={lang} t={t} config={manualConfig} order={manualOrder} quote={pricingQuote} receiptUrl={receiptUrl} busy={busy} canCreate={canCreateOrder} onCreate={createManualPaymentOrder} />}
      {data?.user?.is_advisor && <FounderReview lang={lang} mode="pricing" />}
      <OrderHistory lang={lang} t={t} orders={orders} />
    </div>
  );
}
